import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (product, variant = null, quantity = 1) => {
        // Stock comes from variant (if any), otherwise from product
        const stockRaw = variant?.stock_quantity ?? product.stock_quantity;
        const stock = Number.isFinite(parseInt(stockRaw, 10)) ? parseInt(stockRaw, 10) : null;
        if (stock !== null && stock <= 0) return;     // hard block: out of stock

        const key = variant ? `${product.id}-v${variant.id}` : `${product.id}`;
        const existing = get().items.find(i => i.key === key);
        const currentQty = existing?.quantity || 0;
        // Cap final qty at available stock (if stock is tracked)
        const targetQty = currentQty + quantity;
        const finalQty  = stock !== null ? Math.min(targetQty, stock) : targetQty;
        if (finalQty <= currentQty) {
          // Already at/over stock cap — don't grow
          set({ isOpen: true });
          return;
        }

        if (existing) {
          set(s => ({ items: s.items.map(i => i.key === key ? { ...i, quantity: finalQty, stock } : i) }));
        } else {
          const price = variant ? parseFloat(variant.price) : parseFloat(product.base_price);
          set(s => ({
            items: [...s.items, {
              key, product_id: product.id, variant_id: variant?.id || null,
              name: product.name, variant_label: variant ? `${variant.variant_name}: ${variant.variant_value}` : null,
              price, image: product.primary_image || product.images?.[0]?.image_url,
              slug: product.slug, quantity: finalQty, stock
            }]
          }));
        }
        set({ isOpen: true });
      },

      removeItem: (key) => set(s => ({ items: s.items.filter(i => i.key !== key) })),

      updateQuantity: (key, qty) => {
        if (qty <= 0) { get().removeItem(key); return; }
        set(s => ({
          items: s.items.map(i => {
            if (i.key !== key) return i;
            // Cap at known stock (if tracked)
            const cap = (typeof i.stock === 'number' && i.stock > 0) ? i.stock : qty;
            return { ...i, quantity: Math.min(qty, cap) };
          }),
        }));
      },

      clearCart: () => set({ items: [] }),

      toggleCart: () => set(s => ({ isOpen: !s.isOpen })),
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),

      get subtotal() {
        return get().items.reduce((sum, i) => sum + i.price * i.quantity, 0);
      },
      get count() {
        return get().items.reduce((sum, i) => sum + i.quantity, 0);
      },
    }),
    { name: 'qaya-cart', partialize: s => ({ items: s.items }) }
  )
);

export default useCartStore;
